import React, { useEffect, useRef, useState } from 'react';
import { X, Send } from 'lucide-react';
import dodoUrl from '../assets/dodo.jpg';

const INITIAL_MESSAGES = [
  {
    from: 'bot',
    text: "Hi there! I'm Dodo, your RestoBoxx helper. Ask me anything about menus, orders, tables or staff — or pick one of the quick options below.",
  },
];

const QUICK_PROMPTS = [
  'How do I add a new dish?',
  'How do I take an order?',
  'Where do I manage staff?',
];

function botReply(userText) {
  const q = userText.toLowerCase();
  if (q.includes('dish') || q.includes('menu')) {
    return "Head to Menu → Add Dish from the sidebar. You can set price, category, and a photo there.";
  }
  if (q.includes('order') || q.includes('payment')) {
    return "Open the Orders section in the sidebar. From there you can start a new order, add items, and take payment.";
  }
  if (q.includes('staff') || q.includes('user') || q.includes('role')) {
    return "Go to Staff → All Staff List to invite or manage staff. Use Staff Roles to control permissions.";
  }
  if (q.includes('table') || q.includes('reservation')) {
    return "Use Seating → Designer to draw your floor plan, then Table Selection or Reservations to manage bookings.";
  }
  if (q.includes('hi') || q.includes('hello') || q.includes('hey')) {
    return "Hey! 👋 What can I help you with today?";
  }
  return "I'll note that down — for now, try the quick options below or reach support from Settings → Company Profile.";
}

export default function DodoHelp() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const send = (text) => {
    const value = (text ?? input).trim();
    if (!value) return;
    setMessages((prev) => [...prev, { from: 'user', text: value }]);
    setInput('');
    setTimeout(() => {
      setMessages((prev) => [...prev, { from: 'bot', text: botReply(value) }]);
    }, 450);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {open && (
        <div className="dodo-help-panel" role="dialog" aria-label="Dodo Help">
          <div className="dodo-help-header">
            <img src={dodoUrl} alt="" className="dodo-help-avatar" aria-hidden="true" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">Dodo Help</div>
              <div className="text-[11px] text-white/80">Your RestoBoxx assistant</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="dodo-help-close"
              aria-label="Close help"
            >
              <X size={16} />
            </button>
          </div>

          <div className="dodo-help-messages" ref={listRef}>
            {messages.map((m, i) => (
              <div key={i} className={`dodo-msg dodo-msg-${m.from}`}>
                {m.text}
              </div>
            ))}
          </div>

          <div className="dodo-help-quick">
            {QUICK_PROMPTS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => send(q)}
                className="dodo-help-quick-chip"
              >
                {q}
              </button>
            ))}
          </div>

          <div className="dodo-help-input">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask Dodo a question..."
              className="flex-1 bg-transparent outline-none text-sm"
            />
            <button
              type="button"
              onClick={() => send()}
              disabled={!input.trim()}
              className="dodo-help-send"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`dodo-help-fab ${open ? 'dodo-help-fab-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close help' : 'Open help'}
        title="Need help? Ask Dodo"
      >
        <img src={dodoUrl} alt="Dodo Help" />
        {!open && <span className="dodo-help-badge">?</span>}
      </button>
    </>
  );
}
